import { useState, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';

import {
    setupDefaultConnectionHeaders,
    setAccountConnectionConfig,
    getPrioritizedConnectionConfig,
    getConnectionInfo,
} from '../services/connections';

import { setAccountConnection } from '../store/connection/connectionSlice';

const useConnectionConfigSetup = () => {
    const { instance } = useMsal();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const authState = useSelector((state) => state.connections.authState);
    const [isValidatingConnection, setIsValidatingConnection] = useState(true);
    const [isConnectionValid, setIsConnectionValid] = useState(false);

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    useEffect(() => {
        if (authState === 'success') {
            const config = getPrioritizedConnectionConfig();

            if (config) {
                getConnectionInfo({
                    connectionId: config.connection,
                    environmentId: config.environment,
                    bookId: config.book,
                })
                    .then((response) => {
                        // the provided connection config is valid
                        dispatch(
                            setAccountConnection({
                                ...config,
                                connectionTitle: response.data.connection.description,
                                environmentTitle: response.data.environment.description,
                                bookTitle: response.data.book.description,
                            })
                        );

                        setupDefaultConnectionHeaders();

                        setIsConnectionValid(true);
                        setIsValidatingConnection(false);

                        // update local storage if needed
                        const isDefaultConfig = searchParams.get('isDefaultConfig') === 'true';

                        if (isDefaultConfig) {
                            setAccountConnectionConfig(config);

                            // @todo just remove connection,environment,etc... from search params
                            // and stay on the original route that the user wanted to visit
                            history.replace('/');
                        }
                    })
                    .catch((err) => {
                        const status = err?.response?.status;

                        if (status === 404 || status === 500) {
                            history.replace('/connection');
                        }

                        setIsConnectionValid(false);
                        setIsValidatingConnection(false);
                    });
            } else {
                setIsConnectionValid(false);
                setIsValidatingConnection(false);
            }
        } else if (authState === 'interaction-required' || authState === 'no-users') {
            setIsConnectionValid(false);
            setIsValidatingConnection(false);

            if (authState === 'interaction-required') {
                instance.loginRedirect();
            }
        }
    }, [authState, instance, dispatch, history, searchParams]);

    return {
        isValidatingConnection,
        isConnectionValid,
    };
};

export default useConnectionConfigSetup;
